<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';

  import type { IOptions } from './type';

  export let value: number | string | undefined;
  export let title = '';
  export let activeColor = '';
  export let options: IOptions[] = [];

  let activeTab = 0;
  let tabs: { options: IOptions[]; selected: IOptions | null }[] = [];
  const titleRefs: HTMLElement[] = [];
  let lineTransition = '';

  $: setLinelineTransition(activeTab);
  $: updateTabs(value);
  $: curTabOption = tabs[activeTab];

  /**
   * 标签菜单下方选中条的位置
   */
  const getLinelineTransition = (index: number) => {
    const el = titleRefs[index];
    // onMount之前是拿不到的，避免报错
    if (!el) {
      return '';
    }
    const left = el.offsetLeft + el.offsetWidth / 2;
    return `
      transform: translateX(${left}px) translateX(-50%);
      transition-duration: 0.3s;
    `;
  };

  const setLinelineTransition = (index: number) => {
    lineTransition = getLinelineTransition(index);
  };

  /**
   * 获取当前选中的 tab 信息
   */
  const getSelectedOptionsByValue = (options: IOptions[], value: number | string): IOptions[] | undefined => {
    for (const option of options) {
      if (option.value === value) {
        return [option];
      }

      if (option.children) {
        const selectedOptions = getSelectedOptionsByValue(option.children, value);
        if (selectedOptions) {
          return [option, ...selectedOptions];
        }
      }
    }
  };

  /**
   * 更新 tabs 数组，重新渲染标签列表
   */
  const updateTabs = (value: string | number | undefined) => {
    if (!value) {
      tabs = [
        {
          options,
          // 是否在当前 tab 已有选择
          selected: null,
        },
      ];
      return;
    }

    const selectedOptions = getSelectedOptionsByValue(options, value);
    if (selectedOptions) {
      let optionsCursor = options;
      tabs = selectedOptions.map((option) => {
        const tab = {
          options: optionsCursor,
          selected: option,
        };
        const next = optionsCursor.find(item => item.value === option.value);
        if (next) {
          optionsCursor = next.children;
        }
        return tab;
      });

      if (optionsCursor) {
        tabs.push({
          options: optionsCursor,
          selected: null,
        });
      }

      // 等新的 tab 渲染完再进行动画
      setTimeout(() => {
        activeTab = tabs.length - 1;
      }, 50);
      return;
    }
  };

  /**
   * 初始化操作
   */
  updateTabs(value);
  onMount(() => {
    setLinelineTransition(activeTab);
  });

  /**
   * tab 点击事件处理
   */
  const dispatch = createEventDispatcher();
  const handleClickTab = (curIndex: number) => {
    activeTab = curIndex;
  };

  const handleSelectOption = (curOption: IOptions) => {
    tabs[activeTab].selected = curOption;

    // 重选之后重置后面的 tab
    if (tabs.length > activeTab + 1) {
      tabs = tabs.slice(0, activeTab + 1);
    }

    // 设置之后的 tab 选项数据
    if (curOption.children) {
      const nextTab = {
        options: curOption.children,
        selected: null,
      };
      if (tabs[activeTab + 1]) {
        tabs[activeTab + 1] = nextTab;
      } else {
        tabs.push(nextTab);
      }
    }

    // 更新 value，触发 updateTabs
    const selectedOptions = tabs.map(tab => tab.selected).filter(Boolean);
    value = curOption.value;

    const params = {
      value: curOption.value,
      tabIndex: activeTab,
      selectedOptions,
    };
    dispatch('change', params);

    if (!curOption.children) {
      dispatch('finish', params);
    }
  };
</script>

<div class="cascaser_ctn">
  <div class="header">{title}</div>
  <div class="tabs">
    <div class="tab_nav">
      {#each tabs as tab, index}
        <div
          bind:this={titleRefs[index]}
          class="tab_title"
          class:active={index === activeTab}
          on:click={() => handleClickTab(index)}
          on:keyup={() => handleClickTab(index)}
          role="tab"
          tabindex="-1"
        >
          {tab.selected ? tab.selected.text : '请选择'}
        </div>
      {/each}
      <div class="tab_line" style={lineTransition} style:background={activeColor} />
    </div>
    <div class="tab_content">
      <div role="menu" class="tab_panel">
        {#each curTabOption.options as option}
          <div
            role="menuitemradio"
            class="tab_option"
            class:selected={curTabOption?.selected?.value === option.value}
            tabindex={curTabOption?.selected?.value === option.value ? 0 : -1}
            aria-checked={curTabOption?.selected?.value === option.value}
            on:click={() => handleSelectOption(option)}
            on:keyup={() => {}}
          >
            {option.text}
          </div>
        {/each}
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  .header {
    display: flex;
    align-items: center;
    height: 48px;
    padding: 0 16px;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
  }
  .tab_nav {
    position: relative;
    height: 48px;
    display: flex;
    align-items: center;
    padding: 0 8px 15px;
  }
  .tab_title {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 0 8px;
    font-weight: 400;
    color: #969799;

    :global(.active) {
      font-weight: 600;
      color: #323233;
    }
  }

  .tab_line {
    position: absolute;
    left: 0;
    bottom: 15px;
    width: 40px;
    height: 3px;
    background-color: #ff7000;
    border-radius: 3px;
    z-index: 1;
  }
  .tab_content {
    height: 384px;
    overflow: hidden;
  }
  .tab_panel {
    height: 100%;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    .tab_option {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 16px;
      font-size: 14px;
      color: rgb(50, 50, 51);
      cursor: pointer;
    }
    .selected {
      font-weight: 600;
      color: #ff7000;
    }
  }
</style>
